import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CandidateStatBar } from './CandidateStatBar'
import { IdeologyMini } from '@/components/ideology/IdeologyDisplay'
import { IdeologyAlignmentBadge } from '@/components/ideology/IdeologyAlignmentBadge'
import { RECRUITMENT_SOURCES } from '@/constants/recruitment'
import type { Candidate, IdeologyVector } from '@/types'
import { CheckCircle2 } from 'lucide-react'

interface RecruitmentSuccessProps {
  candidate: Candidate
  partyIdeology: IdeologyVector
  onAssignRole: () => void
  onRecruitAnother: () => void
  onDone: () => void
}

export function RecruitmentSuccess({
  candidate,
  partyIdeology,
  onAssignRole,
  onRecruitAnother,
  onDone,
}: RecruitmentSuccessProps) {
  const sourceConfig = RECRUITMENT_SOURCES[candidate.recruitment_source]

  return (
    <div className="space-y-6">
      {/* Success header */}
      <div className="flex flex-col items-center gap-2 text-center">
        <CheckCircle2 className="h-10 w-10 text-green-500" />
        <h3 className="text-lg font-semibold">Recruitment Successful</h3>
        <p className="text-muted-foreground text-sm">
          <span className="font-medium text-foreground">{candidate.name}</span> has joined your
          party!
        </p>
      </div>

      {/* Candidate details */}
      <div className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold">{candidate.name}</div>
            <div className="text-muted-foreground text-sm">Age: {candidate.age}</div>
          </div>
          <Badge variant="outline">
            {sourceConfig.icon} {sourceConfig.label}
          </Badge>
        </div>

        {/* Stats */}
        <div className="space-y-1.5">
          <CandidateStatBar label="Loyalty" value={candidate.loyalty} colorMode="positive" />
          <CandidateStatBar label="Charisma" value={candidate.charisma} colorMode="positive" />
          <CandidateStatBar label="Admin" value={candidate.admin_skill} colorMode="positive" />
          <CandidateStatBar label="Debate" value={candidate.debate_skill} colorMode="positive" />
        </div>

        <div className="space-y-1.5">
          <CandidateStatBar
            label="Scandal"
            value={candidate.scandal_prone}
            colorMode="negative"
          />
          <CandidateStatBar label="Ambition" value={candidate.ambition} colorMode="neutral" />
        </div>

        {/* Ideology */}
        <div className="border-t pt-3">
          <div className="text-muted-foreground mb-1.5 text-xs font-medium">Ideology</div>
          <div className="flex items-center justify-between gap-2">
            <IdeologyMini ideology={candidate.ideology_vector} />
            <IdeologyAlignmentBadge
              candidateIdeology={candidate.ideology_vector}
              partyIdeology={partyIdeology}
            />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={onDone}>
          Done
        </Button>
        <Button variant="outline" onClick={onRecruitAnother}>
          Recruit Another
        </Button>
        <Button onClick={onAssignRole}>Assign Role</Button>
      </div>
    </div>
  )
}
